import type { CSSProperties, ReactNode } from 'react';
import { useAuthStore } from '@/stores/auth';
import { canSeePrice } from '@/permissions';

// Plan §D-3 (PDF p.17 ②) — "계약 금액에 대해서는 다른 팀에서 볼 수
// 없음." A single component that renders a money value when the
// current user is allowed to see prices, and a redacted placeholder
// otherwise. Centralised here so every price-display site uses the
// same masking rules + visual treatment.
//
// Usage:
//   <MaskedPrice value={deal.expectedRevenue} suffix="원" />
//   <MaskedPrice value={p.totalMonthly} format="abbr" />
//
// The component reads the auth store directly so callers don't have
// to plumb the role through props. If the user object isn't loaded
// yet (auth store hasn't hydrated), MaskedPrice errs on the side of
// hiding — better to flash the redacted state for one frame than
// to leak a price.

export type MaskedPriceFormat =
  /** Pass-through `n.toLocaleString('ko-KR')` (e.g. "5,000,000"). */
  | 'plain'
  /** Korean abbreviation: ≥ 1억 → "N.N억", ≥ 1만 → "N만", else plain. */
  | 'abbr';

interface Props {
  /**
   * BigInt-as-string per decisions §C-6, or `null | undefined` when
   * the field is absent. Numbers are accepted for legacy callers but
   * coerced to string before formatting.
   */
  value: string | number | null | undefined;
  /** Default 'plain' — kanban cards typically pass 'abbr'. */
  format?: MaskedPriceFormat;
  /** Optional suffix appended after the formatted number ("원", "KRW", etc). */
  suffix?: ReactNode;
  /** What to render when value is null/undefined and the user IS allowed to see it. */
  emptyFallback?: ReactNode;
  /** Visually demote redacted text. Default true. */
  muted?: boolean;
  /** Inline style passthrough so callers can keep monospace fonts etc. */
  style?: CSSProperties;
  /** Optional className passthrough. */
  className?: string;
  /** Override the visible-state text — useful for special-case "전 영업 합산" totals. */
  redactLabel?: ReactNode;
}

const REDACT_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  letterSpacing: 0,
  fontSize: '0.95em',
};

export function MaskedPrice({
  value,
  format = 'plain',
  suffix,
  emptyFallback = '-',
  muted = true,
  style,
  className,
  redactLabel = '권한 없음',
}: Props) {
  const user = useAuthStore((s) => s.user);
  const allowed = user ? canSeePrice(user.role) : false;

  if (!allowed) {
    return (
      <span
        className={className}
        style={{
          ...REDACT_BASE,
          color: muted ? 'var(--fg-muted)' : undefined,
          fontStyle: 'italic',
          ...style,
        }}
        aria-label="가격 정보를 볼 권한이 없습니다"
        title="다른 팀에는 계약 금액이 노출되지 않습니다."
      >
        {redactLabel}
      </span>
    );
  }

  if (value === null || value === undefined || value === '') {
    return (
      <span className={className} style={style}>
        {emptyFallback}
      </span>
    );
  }

  const text = formatValue(value, format);
  return (
    <span className={className} style={style}>
      {text}
      {suffix != null && <>{suffix}</>}
    </span>
  );
}

function formatValue(value: string | number, format: MaskedPriceFormat): string {
  const numericString = typeof value === 'number' ? String(value) : value;
  const n = Number(numericString);
  if (!Number.isFinite(n)) return numericString;
  if (format === 'abbr') {
    if (n >= 1e8) return `${(n / 1e8).toFixed(1)}억`;
    if (n >= 1e4) return `${(n / 1e4).toFixed(0)}만`;
    return n.toLocaleString('ko-KR');
  }
  return n.toLocaleString('ko-KR');
}

// Re-export the helper for tests / direct callers that need the same
// formatting without the redact treatment (e.g. CSV export, where the
// price-mask interceptor on the server has already stripped the
// field if needed).
export const formatPriceValue = formatValue;
